import { useState, useEffect, useCallback } from 'react';
import { STEP_RECORDING_STATUS } from '../utils/replayEligibility';

const EXECUTION_STATUS = Object.freeze({
    NEW: 'New',
    PENDING: 'Pending',
    IN_PROGRESS: 'In-Progress',
    PASSED: 'Passed',
    FAILED: 'Failed',
    SKIPPED: 'Skipped',
});

export const useReplayExecution = (
    replayCurrentStepIndex,
    replayExecutionResults,
    replayExecuting,
    executionRecordSteps,
    scrollToTop,
    timerCleanup,
    handleSelectStep
) => {
    const [replayProgressMap, setReplayProgressMap] = useState({});
    const [isReplayStarted, setIsReplayStarted] = useState(false);
    const [isFirstStep, setIsFirstStep] = useState(false);

    // Update progress when current step changes (show step 0 as in-progress before first replayStepStart)
    useEffect(() => {
        if (replayCurrentStepIndex !== null) {
            setReplayProgressMap(prev => ({
                ...prev,
                [replayCurrentStepIndex]: EXECUTION_STATUS.IN_PROGRESS
            }));
        } else if (replayExecuting && executionRecordSteps?.length > 0) {
            setReplayProgressMap(prev => ({
                ...prev,
                [0]: EXECUTION_STATUS.IN_PROGRESS
            }));
        }
    }, [replayCurrentStepIndex, replayExecuting, executionRecordSteps?.length]);

    // Update progress based on results (don't clear during replay - preserves IN_PROGRESS from replayCurrentStepIndex)
    useEffect(() => {
        if (!replayExecutionResults && !replayExecuting) {
            setReplayProgressMap({});
            return;
        }
        const hasResults = replayExecutionResults?.results?.length > 0;
        const hasStepDetails = Object.keys(replayExecutionResults?.stepDetails || {}).length > 0;
        if (replayExecutionResults && executionRecordSteps?.length > 0 && (hasResults || hasStepDetails)) {
            const stepDetails = replayExecutionResults.stepDetails || {};
            const results = replayExecutionResults.results || [];
            const lastResultStepIndex = results.length > 0
                ? Math.max(...results.map((r) => (typeof r.stepIndex === 'number' ? r.stepIndex : Number(r.stepIndex))))
                : -1;

            let firstFailedStepIndex = -1;
            for (const step of executionRecordSteps) {
                const detail = stepDetails[step.stepIndex];
                const detailStatus = detail?.status;
                const isFailed =
                    detailStatus === 'failed' ||
                    (detailStatus === 'completed' && !detail?.result?.result?.success);
                if (isFailed) {
                    firstFailedStepIndex = step.stepIndex;
                    break;
                }
            }

            const stepWithExecuting = executionRecordSteps.find((s) => stepDetails[s.stepIndex]?.status === 'executing');
            const inferredRunningStepIndex = stepWithExecuting?.stepIndex ??
                (replayExecuting && lastResultStepIndex >= 0 ? lastResultStepIndex + 1 : null);
            const effectiveCurrentStepIndex = replayCurrentStepIndex != null && replayCurrentStepIndex >= 0
                ? replayCurrentStepIndex
                : inferredRunningStepIndex;

            const updates = {};
            executionRecordSteps.forEach((step) => {
                if (firstFailedStepIndex >= 0 && step.stepIndex > firstFailedStepIndex) {
                    updates[step.stepIndex] = EXECUTION_STATUS.SKIPPED;
                    return;
                }

                const detail = stepDetails[step.stepIndex];
                const detailStatus = detail?.status;
                const resultForStep = results.find((r) => (
                    typeof r.stepIndex === 'number'
                        ? r.stepIndex === step.stepIndex
                        : Number(r.stepIndex) === step.stepIndex
                ));
                let status;
                if (detailStatus === 'skipped') {
                    status = EXECUTION_STATUS.SKIPPED;
                } else {
                    switch (detailStatus) {
                        case 'completed':
                            status = detail?.result?.result?.success ? EXECUTION_STATUS.PASSED : EXECUTION_STATUS.FAILED;
                            break;
                        case 'new':
                            status = EXECUTION_STATUS.NEW;
                            break;
                        case 'failed':
                            status = EXECUTION_STATUS.FAILED;
                            break;
                        case 'executing':
                            status = EXECUTION_STATUS.IN_PROGRESS;
                            break;
                        default:
                            if (resultForStep) {
                                const successFlag = resultForStep?.result?.result?.success;
                                if (successFlag === false) {
                                    status = EXECUTION_STATUS.FAILED;
                                } else {
                                    status = EXECUTION_STATUS.PASSED;
                                }
                            } else if (effectiveCurrentStepIndex != null && step.stepIndex === effectiveCurrentStepIndex) {
                                status = EXECUTION_STATUS.IN_PROGRESS;
                            } else if (
                                !replayExecuting &&
                                step.stepIndex > lastResultStepIndex &&
                                lastResultStepIndex >= 0
                            ) {
                                status = EXECUTION_STATUS.SKIPPED;
                            } else {
                                status = EXECUTION_STATUS.PENDING;
                            }
                    }
                }
                updates[step.stepIndex] = status;
            });

            setReplayProgressMap(prev => ({ ...prev, ...updates }));
        }
    }, [replayExecutionResults, replayCurrentStepIndex, executionRecordSteps, replayExecuting]);

    useEffect(() => {
        if (
            replayExecutionResults?.results?.length > 0 &&
            executionRecordSteps.length > 0 &&
            !isFirstStep &&
            replayExecuting
        ) {
            setIsFirstStep(true);
            const firstStep = executionRecordSteps[0];
            if (firstStep) {
                handleSelectStep(firstStep);
                if (scrollToTop && timerCleanup) {
                    timerCleanup(() => scrollToTop(), 100);
                }
            }
        }
    }, [
        replayExecutionResults,
        executionRecordSteps,
        isFirstStep,
        replayExecuting,
        handleSelectStep,
        scrollToTop,
        timerCleanup
    ]);

    useEffect(() => {
        if (!replayExecuting) {
            setIsFirstStep(false);
        }
    }, [replayExecuting]);

    const initializeProgressMap = useCallback((steps) => {
        const progressMap = {};
        if (steps?.length > 0) {
            steps.forEach((step) => {
                if (step?.stepIndex === undefined || step?.stepIndex === null) return;
                const recordingStatus = step?.status;
                progressMap[step.stepIndex] =
                    !step?.customStep && recordingStatus === STEP_RECORDING_STATUS.FAILED
                        ? EXECUTION_STATUS.SKIPPED
                        : EXECUTION_STATUS.PENDING;
            });
        }
        setReplayProgressMap(progressMap);
    }, []);

    const resetReplayState = useCallback(() => {
        setReplayProgressMap({});
        setIsReplayStarted(false);
        setIsFirstStep(false);
    }, []);

    return {
        replayProgressMap,
        isReplayStarted,
        setIsReplayStarted,
        initializeProgressMap,
        resetReplayState
    };
};

export { EXECUTION_STATUS };
